import { Injectable, inject } from '@angular/core';
import { map, Observable } from 'rxjs';

import {
  PERSONAL_DATA_REPOSITORY,
  PersonalDataRepository
} from '@app/domain/repositories/personal-data/personal-data.repository.contract';
import { IdentityDocumentPermission } from '@app/entities/enums/personal-data/personal-data.enum';
import { Knowledge } from '@app/entities/interfaces/personal-data/employee-languages.interface';
import { ModifyStudy } from '@app/entities/interfaces/personal-data/studies.interface';
import { TwoFactorAuthorization } from '@app/entities/interfaces/ptrab/two-factor-authorization.interface';
import { PersonalDataAddress } from '@app/entities/models/personal-data/address.model';
import { Child } from '@app/entities/models/personal-data/child.model';
import {
  CertificateEmployeeModel,
  EmployeeLanguages,
  LanguageMastersModel
} from '@app/entities/models/personal-data/employee-languages.model';
import { IdentityDocument } from '@app/entities/models/personal-data/identity-document.model';
import { MaritalStatus } from '@app/entities/models/personal-data/marital-status.model';
import { PersonalDataResponse } from '@app/entities/models/personal-data/personal-data-response.model';
import { PersonalData } from '@app/entities/models/personal-data/personal-data.model';
import {
  Qualification,
  QualificationEducationalLevels,
  QualificationStatuses,
  Study
} from '@app/entities/models/personal-data/studies.model';
import { UpdateEmergencyContact } from '@app/entities/models/personal-data/update-emergency-contact.model';
import { UpdatePhone } from '@app/entities/models/personal-data/update-phone.model';
import { MSafeAny } from '@app/shared/models/safe-any/safe-any.model';

import { PersonalDataUseCases } from './personal-data.use-cases.contract';

@Injectable({
  providedIn: 'root'
})
export class PersonalDataUseCasesImpl implements PersonalDataUseCases {
  #personalData: PersonalDataRepository = inject(PERSONAL_DATA_REPOSITORY);

  getPersonalData(): Observable<PersonalData> {
    return this.#personalData.getPersonalData().pipe(map((personalData) => this.#mapDocumentPermissions(personalData)));
  }

  updateEmail(email: string, authorization: TwoFactorAuthorization): Observable<PersonalDataResponse> {
    return this.#personalData.updateEmail(email, authorization);
  }

  createPersonalPhone(phoneData: UpdatePhone, authorization: TwoFactorAuthorization): Observable<PersonalDataResponse> {
    return this.#personalData.createPersonalPhone(phoneData, authorization);
  }

  updatePersonalPhone(
    updatePhoneData: UpdatePhone,
    phoneId: string,
    authorization: TwoFactorAuthorization
  ): Observable<PersonalDataResponse> {
    return this.#personalData.updatePersonalPhone(updatePhoneData, phoneId, authorization);
  }

  deletePhone(phoneSectionId: string): Observable<PersonalDataResponse> {
    return this.#personalData.deletePhone(phoneSectionId);
  }

  createEmergencyContact(emergencyContactData: UpdateEmergencyContact): Observable<PersonalDataResponse> {
    return this.#personalData.createEmergencyContact(emergencyContactData);
  }

  updateEmergencyContact(
    emergencyContactData: UpdateEmergencyContact,
    phoneId: string
  ): Observable<PersonalDataResponse> {
    return this.#personalData.updateEmergencyContact(emergencyContactData, phoneId);
  }

  getChildren(): Observable<Child[]> {
    return this.#personalData.getChildren();
  }

  createChild(child: Child): Observable<PersonalDataResponse> {
    return this.#personalData.createChild(child);
  }

  updateChild(child: Child, childId: string): Observable<PersonalDataResponse> {
    return this.#personalData.updateChild(child, childId);
  }

  deleteChild(childId: string): Observable<PersonalDataResponse> {
    return this.#personalData.deleteChild(childId);
  }

  updateAddress(address: PersonalDataAddress, authorization: TwoFactorAuthorization): Observable<PersonalDataResponse> {
    return this.#personalData.updateAddress(address, authorization);
  }

  getMaritalStatusOptions(): Observable<MaritalStatus[]> {
    return this.#personalData.getMaritalStatusOptions();
  }

  updateMaritalStatus(id: string, authorization: TwoFactorAuthorization): Observable<object> {
    return this.#personalData.updateMaritalStatus(id, authorization);
  }

  updateIdentityDocument(document: IdentityDocument, authorization: TwoFactorAuthorization): Observable<object> {
    return document.id
      ? this.#personalData.updateIdentityDocument(document, authorization)
      : this.#personalData.createIdentityDocument(document, authorization);
  }

  deleteIdentityDocument(id: string, authorization: TwoFactorAuthorization): Observable<object> {
    return this.#personalData.deleteIdentityDocument(id, authorization);
  }

  getEmployeeLanguages(): Observable<MSafeAny> {
    return this.#personalData.getEmployeeLanguages();
  }

  getLanguagesMasters(): Observable<LanguageMastersModel> {
    return this.#personalData.getLanguagesMasters();
  }

  getLanguagesData(): Knowledge[] | undefined {
    return this.#personalData.getLanguagesData();
  }

  getStudiesData(): Study[] | undefined {
    return this.#personalData.getStudiesData();
  }

  getEmployeeStudies(): Observable<MSafeAny> {
    return this.#personalData.getEmployeeStudies();
  }

  getQualifications(
    countryIsoNumericCode: string,
    educationalLevelId: string,
    page: number,
    name?: string
  ): Observable<Qualification> {
    return this.#personalData.getQualifications(countryIsoNumericCode, educationalLevelId, page, name);
  }

  getQualificationStatuses(): Observable<QualificationStatuses> {
    return this.#personalData.getQualificationStatuses();
  }

  getQualificationEducationalLevels(countryIsoNumericCode: string): Observable<QualificationEducationalLevels> {
    return this.#personalData.getQualificationEducationalLevels(countryIsoNumericCode);
  }

  createEmployeeCertificate(certificate: CertificateEmployeeModel): Observable<EmployeeLanguages> {
    return this.#personalData.createEmployeeCertificate(certificate);
  }

  updateEmployeeCertificate(certificate: CertificateEmployeeModel): Observable<EmployeeLanguages> {
    return this.#personalData.updateEmployeeCertificate(certificate);
  }

  addStudyToEmployee(qualification: ModifyStudy): Observable<object> {
    return this.#personalData.addStudyToEmployee(qualification);
  }

  updateEmployeeStudy(qualification: ModifyStudy): Observable<object> {
    return this.#personalData.updateEmployeeStudy(qualification);
  }

  deleteEmployeeCertificate(languageId: string): Observable<object> {
    return this.#personalData.deleteEmployeeCertificate(languageId);
  }

  deleteStudy(studyId: string, countryIsoNumericCode: string): Observable<object> {
    return this.#personalData.deleteStudy(studyId, countryIsoNumericCode);
  }

  #mapDocumentPermissions(personalData: PersonalData): PersonalData {
    personalData.profile?.identificationDocuments?.forEach((document: IdentityDocument) => {
      document.canEdit = document.permissionType !== null && document.fields.some((field) => field.editable);
      document.canDelete = !document.isPrincipal && document.permissionType === IdentityDocumentPermission.TOTAL;
    });

    return personalData;
  }
}
